import type { AnimationState } from './useTrickAnimation';

interface TrickNavigationBarProps {
  animationState: AnimationState;
  canGoPrevTrick: boolean;
  canGoPrevCard: boolean;
  canGoNextCard: boolean;
  canGoNextTrick: boolean;
  onPrevTrick: () => void;
  onPrevCard: () => void;
  onPlayStop: () => void;
  onNextCard: () => void;
  onNextTrick: () => void;
  /** When true, the entire bar is disabled (greyed out, non-functional) */
  disabled?: boolean;
  /** Whether the stop trick has been reached (affects Play tooltip) */
  hasReachedStopTrick?: boolean;
}

function NavButton({ onClick, disabled, ariaLabel, title, children }: {
  onClick: () => void;
  disabled: boolean;
  ariaLabel: string;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      aria-label={ariaLabel}
      title={title}
      className={`flex items-center justify-center transition-colors counting-nav-btn rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 ${
        disabled
          ? 'text-[#c9c1ab] cursor-not-allowed'
          : 'text-ink hover:text-brass-text cursor-pointer'
      }`}
    >
      {/* The GLYPH carries the size; the BUTTON is free to be bigger than it. They used to be the
          same box, which is why the hit area was whatever the icon happened to measure — 16px on a
          phone, 20 on desktop, both under the 24x24 the constitution takes from WCAG 2.2 AA.
          index.css floors the button at 24 where there is room to; here the icon stays exactly the
          size it was, so nothing looks different. */}
      <span
        className="block"
        style={{ width: 'var(--counting-nav-size, 1.25rem)', height: 'var(--counting-nav-size, 1.25rem)' }}
      >
        {children}
      </span>
    </button>
  );
}

/* ---- SVG Icons ---- */

function PrevTrickIcon() {
  return (
    <svg viewBox="0 0 20 20" fill="currentColor" width="100%" height="100%">
      <rect x="2" y="3" width="3" height="14" rx="0.5" />
      <polygon points="18,3 18,17 7,10" />
    </svg>
  );
}

function PrevCardIcon() {
  return (
    <svg viewBox="0 0 20 20" fill="currentColor" width="100%" height="100%">
      <polygon points="10,3 10,17 1,10" />
      <polygon points="19,3 19,17 10,10" />
    </svg>
  );
}

function PlayIcon() {
  return (
    <svg viewBox="0 0 20 20" fill="currentColor" width="60%" height="60%">
      <polygon points="5,2 18,10 5,18" />
    </svg>
  );
}

function StopIcon() {
  return (
    <svg viewBox="0 0 20 20" fill="currentColor" width="50%" height="50%">
      <rect x="3" y="3" width="14" height="14" rx="1" />
    </svg>
  );
}

function ReplayIcon() {
  return (
    <svg viewBox="0 0 20 20" fill="currentColor" width="60%" height="60%">
      <path d="M4 10a6 6 0 0 1 10.3-4.1L12 8h7V1l-2.6 2.6A8 8 0 1 0 18 10h-2a6 6 0 0 1-12 0z" />
    </svg>
  );
}

function NextCardIcon() {
  return (
    <svg viewBox="0 0 20 20" fill="currentColor" width="100%" height="100%">
      <polygon points="1,3 10,10 1,17" />
      <polygon points="10,3 19,10 10,17" />
    </svg>
  );
}

function NextTrickIcon() {
  return (
    <svg viewBox="0 0 20 20" fill="currentColor" width="100%" height="100%">
      <polygon points="2,3 13,10 2,17" />
      <rect x="15" y="3" width="3" height="14" rx="0.5" />
    </svg>
  );
}

export function TrickNavigationBar({
  animationState,
  canGoPrevTrick,
  canGoPrevCard,
  canGoNextCard,
  canGoNextTrick,
  onPrevTrick,
  onPrevCard,
  onPlayStop,
  onNextCard,
  onNextTrick,
  disabled = false,
  hasReachedStopTrick = false,
}: TrickNavigationBarProps) {
  const isPlaying = animationState === 'PLAYING';
  const isCompleted = animationState === 'COMPLETED';

  const buttonLabel = isPlaying
    ? 'Stop Animation'
    : isCompleted
      ? 'Replay Animation'
      : hasReachedStopTrick
        ? 'Continue Animation'
        : 'Play Animation';
  const ButtonIcon = isPlaying ? StopIcon : isCompleted ? ReplayIcon : PlayIcon;

  return (
    // `counting-nav-bar` is a stable hook for index.css. The rebuilt Counting table draws its
    // controls inside one bordered strip of its own, so the bar's own border is dropped there;
    // no rule targets the class by default, so every legacy consumer is unaffected.
    <div className={`counting-nav-bar border border-line rounded-lg px-2 py-0.5${disabled ? ' opacity-40' : ''}`}>
      <div className="flex items-center justify-center gap-2">
        <NavButton onClick={onPrevTrick} disabled={disabled || isPlaying || !canGoPrevTrick}
          ariaLabel="Previous trick" title="Previous trick">
          <PrevTrickIcon />
        </NavButton>

        <NavButton onClick={onPrevCard} disabled={disabled || isPlaying || !canGoPrevCard}
          ariaLabel="Previous card" title="Previous card">
          <PrevCardIcon />
        </NavButton>

        <button
          onClick={onPlayStop}
          disabled={disabled}
          aria-label={buttonLabel}
          title={buttonLabel}
          className={`flex items-center justify-center transition-opacity counting-nav-play focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 rounded-full ${
            disabled ? 'cursor-not-allowed' : 'cursor-pointer hover:opacity-80'
          }`}
        >
          {/* Same split as the arrows: the green disc is the inner span and keeps its size, so a
              floored hit area grows the tappable box without drawing a bigger circle. */}
          <span
            className="flex items-center justify-center bg-felt rounded-full text-white"
            style={{ width: 'var(--counting-play-size, 2rem)', height: 'var(--counting-play-size, 2rem)' }}
          >
            <ButtonIcon />
          </span>
        </button>

        <NavButton onClick={onNextCard} disabled={disabled || isPlaying || !canGoNextCard}
          ariaLabel="Next card" title="Next card">
          <NextCardIcon />
        </NavButton>

        <NavButton onClick={onNextTrick} disabled={disabled || isPlaying || !canGoNextTrick}
          ariaLabel="Next trick" title="Next trick">
          <NextTrickIcon />
        </NavButton>
      </div>
    </div>
  );
}
